import React from "react";
import { MdCheckCircle, MdError, MdWarning } from "../../onam/icons"; // ONAM glyphs (was react-icons/md)
import { Tooltip } from "../Tooltip";
import InfoIcon from "../../assets/InfoIcon";
import { formatDateTime, getRelativeTimeShort } from "../../utils";
import { HealthIssue, OverallSeverity } from "./types";
import styles from "./systemHealth.module.css";

type StatusBannerProps = {
  severity: OverallSeverity;
  issues: HealthIssue[];
  recordedAt?: string;
  totalChecks: number;
};

const COPY: Record<OverallSeverity, { title: string; Icon: React.ElementType }> = {
  operational: { title: "All Systems Operational", Icon: MdCheckCircle },
  degraded: { title: "Degraded Performance", Icon: MdWarning },
  outage: { title: "Service Outage", Icon: MdError },
};

const StatusBanner: React.FC<StatusBannerProps> = ({
  severity,
  issues,
  recordedAt,
  totalChecks,
}) => {
  const { title, Icon } = COPY[severity];

  return (
    <div className={`${styles.banner} ${styles[`banner-${severity}`]}`}>
      <div className={styles.bannerMain}>
        <Icon className={styles.bannerIcon} />
        <div className={styles.bannerContent}>
          <div className={styles.bannerTitleRow}>
            <div className={styles.bannerTitle}>
              {title}
              {issues.length > 0 && (
                <span className={styles.bannerCount}>
                  {" "}
                  — {issues.length} of {totalChecks} checks failing
                </span>
              )}
            </div>
            <Tooltip
              text="Aggregated across every check on this page: core services, EKS services, indexing, cache, AWS services, SQS queues and CDN distributions. Core Services failing anywhere marks the whole system as an outage; any other failure is degraded."
              maxWidth={280}
            >
              <InfoIcon className={styles.bannerInfoIcon} />
            </Tooltip>
          </div>
          {recordedAt && (
            <div className={styles.bannerMeta}>
              Last recorded{" "}
              <Tooltip
                text={formatDateTime(recordedAt)}
                placement="top"
              >
                <span className={styles.bannerTimeValue}>
                  {getRelativeTimeShort(new Date(recordedAt))}
                </span>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      {issues.length > 0 && (
        <div className={styles.bannerIssueList}>
          {issues.map((issue, idx) => (
            <span key={idx} className={styles.bannerIssueChip}>
              <strong>{issue.group}:</strong> {issue.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusBanner;
